// src/firebase.js
import { initializeApp } from "firebase/app";
import {
  getAuth,
  setPersistence,
  browserLocalPersistence,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  onAuthStateChanged,
  signOut,
} from "firebase/auth";
import {
  getFirestore,
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";
import { getStorage } from "firebase/storage";
import { getFunctions } from "firebase/functions"; // ✅ ajout

// (Optionnel) App Check – à activer si tu veux verrouiller Storage aux clients légitimes
// import { initializeAppCheck, ReCaptchaV3Provider } from "firebase/app-check";

// === CONFIG ===
// Note: le nom de bucket Firebase Storage est toujours <projectId>.appspot.com
const firebaseConfig = {
  apiKey: process.env.REACT_APP_API_KEY || "",
  authDomain:
    process.env.REACT_APP_AUTH_DOMAIN || "plateforme-educative-2025.firebaseapp.com",
  projectId: process.env.REACT_APP_PROJECT_ID || "plateforme-educative-2025",

  // ✅ bucket correct
  storageBucket:
    process.env.REACT_APP_STORAGE_BUCKET || "plateforme-educative-2025.appspot.com",

  messagingSenderId: process.env.REACT_APP_MESSAGING_SENDER_ID || "289758217407",
  appId: process.env.REACT_APP_APP_ID || "1:289758217407:web:6d03d04b3de342f6a2f20a",
};

const app = initializeApp(firebaseConfig);

// (Optionnel) App Check – décommente si tu as une clé reCAPTCHA v3 et active ensuite l’enforcement pour Storage
// initializeAppCheck(app, {
//   provider: new ReCaptchaV3Provider("TON_RECAPTCHA_V3_SITE_KEY"),
//   isTokenAutoRefreshEnabled: true,
// });

// SDKs
export const auth = getAuth(app);
export const db = getFirestore(app);

// ✅ on force explicitement le bucket standard (gs://<project>.appspot.com)
export const storage = getStorage(
  app,
  "gs://plateforme-educative-2025.appspot.com"
);

// ✅ export Functions (région us-central1 pour ta Function v2)
export const functions = getFunctions(app, "us-central1");

// Persistance locale (reste connecté après refresh)
setPersistence(auth, browserLocalPersistence);

// Provider Google
const provider = new GoogleAuthProvider();
provider.setCustomParameters({ prompt: "select_account" });

// --- Emails profs -> matière
export const TEACHER_SUBJECT_BY_EMAIL = {
  "o.radey4001@gmail.com": "Physique-Chimie",
  "boussoufelmostafa@gmail.com": "Mathématiques",
  "fazazi.naoufel@gmail.com": "Philosophie",
  "omarougazza@gmail.com": "Anglais",
  "badrfath16@gmail.com": "SVT",
  // ✅ Nouveaux profs 3AC Fr (EI, HG, Arabe)
  "idrissi.oukacha@gmail.com": "Éducation islamique",
  "belhassan11@gmail.com": "Histoire-Géographie",
  "samir.moumni01@gmail.com": "Arabe",
  // ✅ Français 3AC Fr
  "badr16fath@gmail.com": "Français",
};

// ✅ Emails des administrateurs (peuvent accéder à toutes les pages protégées)
export const ADMIN_EMAILS = new Set([
  "badrfath16@gmail.com", // SVT
  "boussoufelmostafa@gmail.com", // Math
]);

// Crée/complète le profil Firestore si besoin
export async function ensureProfile(user) {
  const email = (user.email || "").toLowerCase();
  const subject = TEACHER_SUBJECT_BY_EMAIL[email] || null;
  const isProf = !!subject;
  const isAdmin = ADMIN_EMAILS.has(email);

  const ref = doc(db, "users", user.uid);
  const snap = await getDoc(ref);

  const base = {
    uid: user.uid,
    email: user.email || null,
    displayName: user.displayName || null,
    role: isAdmin ? "admin" : (isProf ? "prof" : "eleve"),
    subject,
    access: isProf ? "approved" : "pending",
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  if (!snap.exists()) {
    await setDoc(ref, base, { merge: true });
  } else {
    // 🚩 On aligne le rôle sur l'email (admin/prof/eleve) et on maintient la matière détectée
    await setDoc(
      ref,
      {
        role: base.role,
        subject: subject ?? null,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  }
}

// --- Auth helpers ---
export async function signInWithGoogleOnly() {
  try {
    const res = await signInWithPopup(auth, provider);
    await ensureProfile(res.user);
    return res.user;
  } catch (e) {
    if (
      e.code === "auth/popup-blocked" ||
      e.code === "auth/cancelled-popup-request"
    ) {
      await signInWithRedirect(auth, provider);
      return;
    }
    throw e;
  }
}

export function watchAuth(cb) {
  return onAuthStateChanged(auth, cb);
}

export async function logout() {
  await signOut(auth);
}

export default app;
